import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck, X } from "lucide-react";

export default function PixCheckoutModal({ plan, onClose, onPaid }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!plan) return;
    setError(null);
    setLoading(false);
  }, [plan?.key]);

  if (!plan) return null;

  const goToCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/payments/checkout", { plan: plan.key });
      const url = data?.url;
      if (!url) throw new Error("Link de pagamento não retornado");
      window.location.href = url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Não foi possível gerar o link de pagamento");
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-md border-slate-800 text-slate-100 sm:max-w-md" data-testid="infinitepay-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-100">
            {`Assinar ${plan.label}`}
          </DialogTitle>
          <button onClick={onClose} data-testid="pix-modal-close-button"
            className="absolute right-4 top-4 text-slate-500 hover:text-slate-300 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">{plan.label}</span>
            <span className="font-mono font-bold text-emerald-400 text-lg">
              R$ {plan.price?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </span>
          </div>

          <p className="text-sm text-slate-400">
            Você será redirecionado para o checkout seguro da InfinitePay (Pix e cartão na mesma página). O acesso é liberado automaticamente após a confirmação.
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300" data-testid="checkout-error">
              {error}
            </div>
          )}

          <Button onClick={goToCheckout} disabled={loading} data-testid="btn-go-infinitepay"
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando link...</>) : (<><ExternalLink className="w-4 h-4 mr-2" /> Ir para pagamento</>)}
          </Button>

          <div className="border-t border-slate-800 pt-4 space-y-3" data-testid="checkout-trust-badges">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-[10px] font-extrabold italic px-2.5 py-1 rounded-md bg-[#1A1F71] text-white tracking-wide">VISA</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700">Mastercard</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#00A4E0] text-white">Elo</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#32BCAD] text-white">Pix</span>
            </div>
            <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5 text-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              Ambiente seguro. Pagamento processado via InfinitePay.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
